// Un post que ya salió en Instagram — ADR-2026-10-30-el-post-publicado-se-mira-no-se-edita.
//
// Instagram no deja cambiar las fotos ni el texto de un post publicado (su API sólo prende o apaga
// los comentarios), así que no abre el editor: abre el post para mirarlo. Arriba, cómo le fue;
// abajo, lo que salió (piezas, texto y cuándo) y la vista previa, sin nada que editar.

import { useEffect, useState } from 'react';
import { AlignLeft, CalendarClock, Check, ChevronLeft, Copy, Download, Images, Link2, Loader2, Play } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import { useBrandProfile } from '../../../hooks/useBrandProfile';
import { descargarSueltos } from '../../../utils/descargarLote';
import { boton } from '../botones';
import { fechaLarga } from './EditorDePost';
import { firmarFull } from '../lanzamientosApi';
import { Seccion } from '../Seccion';
import VisorDeFoto from '../VisorDeFoto';
import { nombresDeLasPiezas } from './descargarPost';
import { ComoLeFue } from './metricas/ComoLeFue';
import { formatoDe, nombreDelFormato } from './post';
import type { PostConMiniaturas } from './postsApi';
import { VistaPreviaDeInstagram } from './VistaPreviaDeInstagram';

export function PostPublicado({
  post,
  companyId,
  onCerrar,
}: {
  post: PostConMiniaturas;
  companyId: string;
  onCerrar: () => void;
}) {
  const { activeBrand, profile, currentBrandId, loading: cargandoMarca } = useBrandProfile();
  const [borradoEnInstagram, setBorradoEnInstagram] = useState(false);
  const [bajando, setBajando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  /** La pieza abierta en grande (un video se reproduce). La URL completa se firma recién al abrirla. */
  const [viendo, setViendo] = useState<string | null>(null);
  const [urlAmpliada, setUrlAmpliada] = useState<string | null>(null);
  useEffect(() => {
    if (!viendo) return setUrlAmpliada(null);
    setAviso(null);
    const m = post.medios.find((x) => x.id === viendo);
    if (!m) return;
    let sigue = true;
    void firmarFull(companyId, m.fileName).then((u) => {
      if (!sigue) return;
      // Sin URL el visor quedaría cargando para siempre: se cierra y se dice, para reintentar tocándola.
      if (!u) {
        setViendo(null);
        setAviso('No se pudo abrir esa pieza. Tocala de nuevo para reintentar.');
        return;
      }
      setUrlAmpliada(u);
    });
    return () => {
      sigue = false;
    };
  }, [viendo, companyId, post.medios]);

  const descargar = async () => {
    if (post.medios.length === 0 || bajando) return;
    setBajando(true);
    setAviso(null);
    const nombres = nombresDeLasPiezas(post.medios, post.cuando);
    const urls = await Promise.all(post.medios.map((m) => firmarFull(companyId, m.fileName)));
    const archivos = post.medios
      .map((m, i) => ({ url: urls[i] ?? '', nombre: nombres[i] ?? 'post', imagenId: m.generadaId ?? undefined }))
      .filter((a) => a.url);
    // Las que no se pudieron firmar se suman: un carrusel incompleto no dice "todo bien".
    const sinFirmar = post.medios.length - archivos.length;
    const r = await descargarSueltos(archivos);
    const fallidos = r.fallidos + sinFirmar;
    setBajando(false);
    if (r.ok === 0) setAviso('No se pudo descargar');
    else if (fallidos > 0) setAviso(`Se bajaron ${r.ok}; ${fallidos} fallaron`);
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(post.texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1800);
    } catch {
      setAviso('El navegador no dejó copiar. Seleccioná el texto y copialo a mano.');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-y-3">
        {/* Visible también en el teléfono: el editor vuelve con "Guardar post", que acá no existe. */}
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Volver al calendario"
          title="Volver al calendario"
          className="-ml-1 mr-1 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-brand-black hover:bg-neutral-100"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold text-brand-black md:text-2xl">Post publicado</h1>
        <span className="ml-3 shrink-0 rounded-full bg-neutral-100 py-1 px-2.5 text-xs font-medium text-neutral-600">
          Publicado
        </span>
      </div>
      <p className="mt-1 text-sm text-neutral-500 md:pl-7">
        Instagram · {nombreDelFormato(formatoDe(post.medios))}
        {post.cuando ? ` · salió el ${fechaLarga(post.cuando)}` : ''}
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {post.permalink && !borradoEnInstagram && (
          <a href={post.permalink} target="_blank" rel="noopener noreferrer" className={boton('primario', { chico: true, extra: 'py-2' })}>
            <Link2 className="w-3.5 h-3.5" /> Ver en Instagram
          </a>
        )}
        <button
          type="button"
          onClick={() => void descargar()}
          disabled={post.medios.length === 0 || bajando}
          className={boton('secundario', { chico: true, extra: 'py-2' })}
        >
          {bajando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {bajando ? 'Bajando…' : 'Descargar'}
        </button>
        <button
          type="button"
          onClick={() => void copiar()}
          disabled={!post.texto}
          className={boton('secundario', { chico: true, extra: 'py-2' })}
        >
          {copiado ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copiado ? 'Copiada' : 'Copiar descripción'}
        </button>
      </div>
      {aviso && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 py-3 px-4 text-sm text-amber-900">{aviso}</p>
      )}

      {/* Hasta saber la marca no se lee: con la marca en null la edge leería la de por defecto. */}
      {post.igMediaId && !cargandoMarca && (
        <ComoLeFue grande brandId={currentBrandId} mediaId={post.igMediaId} onBorrado={setBorradoEnInstagram} />
      )}

      <div className="mt-5 grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <Seccion titulo="Lo que salió" icono={<Images className="w-4 h-4" />} plegable={false}>
            <div className="flex flex-wrap gap-2">
              {post.medios.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setViendo(m.id)}
                  aria-label={m.esVideo ? `Reproducir el video ${i + 1}` : `Ver la foto ${i + 1} en grande`}
                  className="relative grid h-24 w-24 place-items-center overflow-hidden rounded-xl bg-neutral-100 transition-transform duration-150 ease-out motion-safe:active:scale-[0.96]"
                >
                  {post.miniaturas[i] ? (
                    <CachedImg src={post.miniaturas[i]} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Play className="w-4 h-4 text-neutral-400" />
                  )}
                  {m.esVideo && post.miniaturas[i] && (
                    <span className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-md bg-black/35 text-white">
                      <Play className="w-3.5 h-3.5" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </Seccion>
          <Seccion titulo="Descripción" icono={<AlignLeft className="w-4 h-4" />} plegable={false}>
            <p className="whitespace-pre-wrap text-sm text-brand-black">{post.texto || 'Sin descripción.'}</p>
          </Seccion>
          <Seccion titulo="Cuándo salió" icono={<CalendarClock className="w-4 h-4" />} plegable={false}>
            <p className="text-sm text-brand-black first-letter:uppercase">{post.cuando ? fechaLarga(post.cuando) : '—'}</p>
            <p className="mt-1 text-xs text-neutral-500">
              Instagram no deja cambiar un post que ya salió. Para otra versión, armá un post nuevo.
            </p>
          </Seccion>
        </div>
        <VistaPreviaDeInstagram
          medios={post.medios}
          miniaturas={post.miniaturas}
          texto={post.texto}
          cuando={post.cuando}
          marca={activeBrand?.name || profile?.company_name || ''}
          logo={activeBrand?.logo_url ?? null}
        />
      </div>

      {viendo && (
        <VisorDeFoto
          url={urlAmpliada}
          esVideo={!!post.medios.find((m) => m.id === viendo)?.esVideo}
          onCerrar={() => setViendo(null)}
        />
      )}
    </div>
  );
}
